/**
 * Status constants for various entities across the application
 * These constants should be kept in sync with the API enums
 */

export const RenderStatus = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
} as const;

export type RenderStatusType = (typeof RenderStatus)[keyof typeof RenderStatus];

export const ControlStatus = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
} as const;

export type ControlStatusType =
  (typeof ControlStatus)[keyof typeof ControlStatus];

export const UpscaleStatus = {
  NOT_STARTED: 'not_started',
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
} as const;

export type UpscaleStatusType =
  (typeof UpscaleStatus)[keyof typeof UpscaleStatus];

export const ValidationStatus = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
} as const;

export type ValidationStatusType =
  (typeof ValidationStatus)[keyof typeof ValidationStatus];

export const AngleValidationStatus = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  VALID: 'valid',
  INVALID: 'invalid',
  TECHNICAL_ERROR: 'technical_error',
  PROHIBITED: 'prohibited',
  // New terminal status (P5.1): different building than the project's angles.
  // Behaves like INVALID — finished + needs replacement.
  DIFFERENT_BUILDING: 'different_building',
  // Brief-intake Phase 3: the image was a drawing/document and was auto-routed
  // into the Brief documents. A NEUTRAL/POSITIVE terminal outcome (the file
  // moved photos→documents), NOT a rejection.
  INFO_SOURCE: 'info_source',
  // Brief-intake Phase 3: the image was inspiration material (not this building)
  // and was auto-routed into the References section. Also a neutral/positive
  // "moved photos→references" terminal outcome, NOT a rejection.
  REFERENCE: 'reference',
  // The image was an artistic/illustrative rendering of a building (hand-drawn
  // sketch, watercolour, stylised illustration) and was auto-routed into the
  // References section as an output-style reference (category=output). Same
  // neutral/positive "moved photos→output-style" terminal outcome, NOT a
  // rejection — mirrors REFERENCE.
  OUTPUT_STYLE: 'output_style',
} as const;

export type AngleValidationStatusType =
  (typeof AngleValidationStatus)[keyof typeof AngleValidationStatus];

/**
 * True when the API has finished validating — one of valid, invalid,
 * technical_error, or prohibited. UI should stop showing shimmers/spinners
 * for these rows. Mirrors AngleValidationStatusEnum::isFinished() on the API.
 */
export function isAngleValidationFinished(
  status: AngleValidationStatusType | string | undefined
): boolean {
  return (
    status === AngleValidationStatus.VALID ||
    status === AngleValidationStatus.INVALID ||
    status === AngleValidationStatus.TECHNICAL_ERROR ||
    status === AngleValidationStatus.PROHIBITED ||
    status === AngleValidationStatus.DIFFERENT_BUILDING ||
    status === AngleValidationStatus.INFO_SOURCE ||
    status === AngleValidationStatus.REFERENCE ||
    status === AngleValidationStatus.OUTPUT_STYLE
  );
}

export const PaymentStatus = {
  PENDING: 'pending',
  REQUIRES_ACTION: 'requires_action',
  PROCESSING: 'processing',
  SUCCEEDED: 'succeeded',
  FAILED: 'failed',
  CANCELED: 'canceled',
  EXPIRED: 'expired',
  REFUNDED: 'refunded',
  // Entitlement revoked by the store without a money refund (Apple family
  // revoke, Google SUBSCRIPTION_REVOKED). Matches API PaymentStatusEnum.
  REVOKED: 'revoked',
} as const;

export type PaymentStatusType =
  (typeof PaymentStatus)[keyof typeof PaymentStatus];

export const AlbumGenerationStatus = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
} as const;

export type AlbumGenerationStatusType =
  (typeof AlbumGenerationStatus)[keyof typeof AlbumGenerationStatus];

export const UploadStatus = {
  PENDING: 'pending',
  UPLOADING: 'uploading',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
  PAUSED: 'paused',
} as const;

export type UploadStatusType = (typeof UploadStatus)[keyof typeof UploadStatus];

export const SubscriptionStatus = {
  INCOMPLETE: 'incomplete',
  INCOMPLETE_EXPIRED: 'incomplete_expired',
  ACTIVE: 'active',
  PAST_DUE: 'past_due',
  CANCELED: 'canceled',
  ENDED: 'ended',
} as const;

export type SubscriptionStatusType =
  (typeof SubscriptionStatus)[keyof typeof SubscriptionStatus];
